import { forwardRef, useId, type InputHTMLAttributes } from 'react'

// P009: thin uncontrolled wrapper around <input type="file">, mirroring
// Input.tsx's label/error/hint API — NOT the FileUpload dropzone (no
// drag-drop, progress, or preview; that's FileUpload's job). The caller owns
// the FileList via onChange. The native "Choose file" button text is not
// themeable via CSS — accepted limitation for every caller that does NOT
// opt into the overlay below.
export interface FileInputProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: string
  error?: string
  hint?: string
  // Only two sizes — DaisyUI's file-input has no xs/lg use case here.
  size?: 'sm' | 'md'
  /**
   * F10/AC-8 (EP-158): the browser's own "Choose File" / "No file chosen"
   * text is OS-locale text, not CSS-themeable (there is no `content:`
   * override for a `::file-selector-button`'s label) — a Thai screen with
   * `size="md"` otherwise always reads English regardless of i18n. Passing
   * `chooseLabel` opts a caller INTO a custom-labelled trigger: the native
   * input is kept (still the thing clicked, via a `<label htmlFor>` — no
   * absolute-position/opacity overlay, no synthetic click proxy) but
   * visually hidden, replaced by a styled button reading `chooseLabel` plus
   * a text span showing `selectedFileName` or `emptyLabel`. Omit (default)
   * to keep the original native rendering — every existing caller is
   * unaffected.
   */
  chooseLabel?: string
  /** Shown beside the trigger when no file is selected. Required with `chooseLabel`. */
  emptyLabel?: string
  /** The currently selected file's name, for the overlay's filename span. */
  selectedFileName?: string | null
}

const SIZE_CLASSES: Record<NonNullable<FileInputProps['size']>, string> = {
  sm: 'file-input-sm',
  md: '',
}

export const FileInput = forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      className = '',
      label,
      error,
      hint,
      id,
      required,
      size = 'md',
      chooseLabel,
      emptyLabel,
      selectedFileName,
      ...props
    },
    ref,
  ) => {
    // A caller relying on the overlay needs a stable id to associate the
    // trigger <label> with the input even when it passes neither id nor
    // name (useId() guarantees one; the plain-native branch below never
    // needed this, so it keeps the original id-or-name-or-undefined shape).
    const generatedId = useId()
    const inputId = id || props.name || (chooseLabel !== undefined ? generatedId : undefined)

    if (chooseLabel !== undefined) {
      return (
        <div className={`form-control ${className || 'w-full'}`}>
          {label && (
            <label className="label whitespace-normal" htmlFor={inputId}>
              <span className="label-text">
                {label}
                {required && <span className="text-error ml-0.5" aria-hidden="true">*</span>}
              </span>
            </label>
          )}
          <div className="flex items-center gap-3">
            {/* Clicking a <label> whose `for` names a file input opens that
                input's native file picker — standard HTML behaviour, so no
                synthetic .click() proxy is needed. The input stays in the
                DOM and keeps receiving the real change event; it is only
                visually hidden (sr-only), never removed or disabled, so it
                stays in the tab order and announced correctly. */}
            <label
              htmlFor={inputId}
              className={`btn btn-outline ${SIZE_CLASSES[size] === 'file-input-sm' ? 'btn-sm' : ''} ${error ? 'btn-error' : ''}`.trim()}
            >
              {chooseLabel}
            </label>
            <span className="text-sm text-base-content/70 truncate" data-testid="file-input-filename">
              {selectedFileName || emptyLabel}
            </span>
          </div>
          <input
            ref={ref}
            id={inputId}
            type="file"
            required={required}
            className="sr-only"
            {...props}
          />
          {(error || hint) && (
            <label className="label whitespace-normal">
              <span className={`label-text-alt ${error ? 'text-error' : ''}`}>
                {error || hint}
              </span>
            </label>
          )}
        </div>
      )
    }

    return (
      <div className={`form-control ${className || 'w-full'}`}>
        {label && (
          <label className="label whitespace-normal" htmlFor={inputId}>
            <span className="label-text">
              {label}
              {required && <span className="text-error ml-0.5" aria-hidden="true">*</span>}
            </span>
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          type="file"
          required={required}
          className={`file-input file-input-bordered w-full ${SIZE_CLASSES[size]} ${error ? 'file-input-error' : ''}`}
          {...props}
        />
        {(error || hint) && (
          <label className="label whitespace-normal">
            <span className={`label-text-alt ${error ? 'text-error' : ''}`}>
              {error || hint}
            </span>
          </label>
        )}
      </div>
    )
  }
)

FileInput.displayName = 'FileInput'
